/**
 * L'audio parte alla velocita' che il server si aspetta, non a quella che
 * decide l'hardware.
 *
 * IL PUNTO: il registratore chiede 48 kHz a `getUserMedia`, ma i campioni che
 * arrivano a `onaudioprocess` sono alla frequenza dell'`AudioContext`, che il
 * browser sceglie dal dispositivo audio di sistema. Su un Mac (o un iPhone)
 * con uscita a 44,1 kHz il contesto nasce a 44100 e nessuno lo rinegozia:
 * `getUserMedia` non impone la frequenza del contesto.
 *
 * A valle nessuno chiede: il server fa `_dimezza` (48 -> 24 kHz, media di ogni
 * coppia) e dichiara a OpenAI `{"type": "audio/pcm", "rate": 24000}`;
 * `createWavBlob` scrive 48000 nell'intestazione. Con un contesto a 44,1 kHz
 * la voce arriva al motore e nel file l'8,8% piu' veloce e piu' acuta, per
 * tutta la registrazione.
 *
 * Interpolazione lineare, blocco per blocco e senza stato: al confine fra due
 * blocchi si perde la pendenza dell'ultimo campione, cioe' un errore di un
 * intervallo di campionamento ogni 4096 — quattro ordini di grandezza sotto
 * l'8,8% che toglie.
 */

/** Quello che il server assume, in `_dimezza` e nell'intestazione del WAV. */
export const HZ_DESTINAZIONE = 48000;

/**
 * @param {Float32Array} blocco  i campioni come li da' `onaudioprocess`
 * @param {number} hzSorgente    `audioContext.sampleRate`
 * @returns {Float32Array}       lo stesso blocco a 48 kHz (l'originale se lo e' gia')
 */
export const ricampiona = (blocco, hzSorgente) => {
  if (!blocco || blocco.length === 0) return blocco;
  if (!hzSorgente || hzSorgente === HZ_DESTINAZIONE) return blocco;

  const rapporto = HZ_DESTINAZIONE / hzSorgente;
  const lunghezza = Math.max(1, Math.round(blocco.length * rapporto));
  const uscita = new Float32Array(lunghezza);
  const ultimo = blocco.length - 1;

  for (let i = 0; i < lunghezza; i++) {
    const posizione = i / rapporto;
    const giu = Math.floor(posizione);
    const frazione = posizione - giu;
    const a = blocco[Math.min(giu, ultimo)];
    const b = blocco[Math.min(giu + 1, ultimo)];
    uscita[i] = a + (b - a) * frazione;
  }

  return uscita;
};

export default ricampiona;
